import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import { FiX, FiArrowRight, FiArrowLeft, FiCheck } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useGlobal } from '../Context/GlobalContext';
import { useLanguage } from '../Context/LanguageContext';
import './LeadsSetupTour.css';
import OutlinedSecondaryButton from './Buttons/OutlinedSecondaryButton';
import LinkButton from './Buttons/LinkButton';
import PrimaryButton from './Buttons/PrimaryButton';

const LeadsSetupTour = ({ show, onClose, onComplete, chatbotId }) => {
  const navigate = useNavigate();
  const { contentDomain } = useGlobal();
  const { t } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Construct GIF URL with fallback
  const gifUrl = useMemo(() => {
    const domain = contentDomain || 'https://content.brandvoxai.com';
    return `${domain}/assets/images/leads-tour/add-and-connect-leads.gif`;
  }, [contentDomain]);

  // Effect to highlight Action Map in sidebar - DISABLED
  useEffect(() => {
    if (!show) return;

    // Cleanup on unmount or when modal closes
    return () => {
      const highlightedElements = document.querySelectorAll('.tour-highlight-pulse');
      highlightedElements.forEach(el => el.classList.remove('tour-highlight-pulse'));
    };
  }, [show]);

  const slides = [
    {
      title: t('components.leadssetuptour.welcomeTitle'),
      description: t('components.leadssetuptour.welcomeDescription'),
      image: null,
      imageAlt: 'Welcome illustration',
      noImage: true,
      compact: true
    },
    {
      title: t('components.leadssetuptour.step1Title'),
      description: t('components.leadssetuptour.step1Description'),
      image: null,
      imageAlt: 'Action Map demonstration',
      highlight: t('components.leadssetuptour.step1Highlight'),
      noImage: true,
      compact: true
    },
    {
      title: t('components.leadssetuptour.step2Title'),
      description: t('components.leadssetuptour.step2Description'),
      image: gifUrl,
      imageAlt: 'Adding and connecting leads node',
      highlight: t('components.leadssetuptour.step2Highlight')
    },
    {
      title: t('components.leadssetuptour.step3Title'),
      description: t('components.leadssetuptour.step3Description'),
      image: gifUrl,
      imageAlt: 'Adding and connecting leads node',
      highlight: t('components.leadssetuptour.step3Highlight')
    },
    {
      title: t('components.leadssetuptour.step4Title'),
      description: t('components.leadssetuptour.step4Description'),
      image: gifUrl,
      imageAlt: 'Adding and connecting leads node',
      highlight: t('components.leadssetuptour.step4Highlight')
    },
    {
      title: t('components.leadssetuptour.step5Title'),
      description: t('components.leadssetuptour.step5Description'),
      image: gifUrl,
      imageAlt: 'Adding and connecting leads node',
      highlight: t('components.leadssetuptour.step5Highlight')
    },
    {
      title: t('components.leadssetuptour.finalTitle'),
      description: t('components.leadssetuptour.finalDescription'),
      image: null,
      imageAlt: 'Setup complete',
      cta: true,
      noImage: true,
      compact: true
    }
  ];

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  const handleFinish = () => {
    onComplete();
    // Navigate to action flow page
    navigate(`/agent/${chatbotId}/action-flow`);
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;
  const isFirstSlide = currentSlide === 0;
  const showSidebarSpotlight = false; // Sidebar spotlight disabled

  return (
    <>
      {/* Custom backdrop with sidebar spotlight */}
      {show && (
        <div className={`tour-custom-backdrop ${showSidebarSpotlight ? 'show-sidebar' : ''}`} />
      )}

      <Modal
        show={show}
        onHide={handleSkip}
        centered
        size="lg"
        className="leads-setup-tour-modal"
        backdrop={false}
      >
        <Modal.Header className="border-0 pb-0">
          <button
            className="tour-close-btn"
            onClick={handleSkip}
            aria-label={t('components.leadssetuptour.closeTour')}
          >
            <FiX size={20} />
          </button>
        </Modal.Header>

        <Modal.Body className="pt-2">
          <div className={`tour-slide-container ${currentSlideData.compact ? 'compact' : ''}`}>
            {/* Slide content */}
            <div className="tour-slide-content">
              {!currentSlideData.noTitle && (
                <h3 className="tour-slide-title">{currentSlideData.title}</h3>
              )}
              <p className="tour-slide-description">{currentSlideData.description}</p>

              {currentSlideData.highlight && (
                <div className="tour-slide-highlight">
                  <span className="highlight-icon">💡</span>
                  {currentSlideData.highlight}
                </div>
              )}

              {/* Image/GIF placeholder */}
              {!currentSlideData.noImage && (
                <div className="tour-slide-image-container">
                  <img
                    src={currentSlideData.image}
                    alt={currentSlideData.imageAlt}
                    className="tour-slide-image"
                    onError={(e) => {
                      // Fallback if image doesn't exist yet
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="tour-slide-placeholder">
                    <div className="placeholder-icon">🖼️</div>
                    <p className="placeholder-text">{currentSlideData.imageAlt}</p>
                    <small className="placeholder-hint">
                      {t('components.leadssetuptour.screenshotPlaceholder')}
                    </small>
                  </div>
                </div>
              )}
            </div>

            {/* Progress dots */}
            <div className="tour-progress-dots">
              {slides.map((_, index) => (
                <button
                  key={index}
                  className={`tour-dot ${index === currentSlide ? 'active' : ''} ${index < currentSlide ? 'completed' : ''}`}
                  onClick={() => handleDotClick(index)}
                  aria-label={t('components.leadssetuptour.goToSlide', { number: index + 1 })}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="tour-navigation">
              <div className="tour-nav-left">
                {!isFirstSlide && (
                  <OutlinedSecondaryButton
                    onClick={handlePrev}
                    mobileWidth="100%"
                  >
                    <FiArrowLeft className="me-2" />
                    {t('components.leadssetuptour.previous')}
                  </OutlinedSecondaryButton>
                )}
              </div>

              <div className="tour-nav-right">
                {!isLastSlide && (
                  <LinkButton
                    mobileWidth="100%"
                    onClick={handleSkip}
                  >
                    {t('components.leadssetuptour.skipTour')}
                  </LinkButton>
                )}

                {isLastSlide ? (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleFinish}
                  >
                    <FiCheck className="me-2" />
                    {t('components.leadssetuptour.goToActionMap')}
                  </PrimaryButton>
                ) : (
                  <PrimaryButton
                    mobileWidth="100%"
                    onClick={handleNext}
                  >
                    {t('components.leadssetuptour.next')}
                    <FiArrowRight className="ms-2" />
                  </PrimaryButton>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LeadsSetupTour;
